import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { navigate } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
const LanguageContext = createContext({});
const expandDatoLocaleKey = (pageContext, allLangs, defaultLang) => {
    const defaultContext = {
        slug: defaultLang.entryPointSlug,
        datoLocale: defaultLang.datoLocale,
        lang: defaultLang,
    };
    if (pageContext) {
        if ('id' in pageContext && typeof pageContext.slug === 'string') {
            defaultContext.slug = pageContext.slug;
        }
        if ('datoLocale' in pageContext && typeof pageContext.datoLocale === 'string') {
            const matchedLangItem = allLangs.find(x => x.datoLocale === pageContext.datoLocale);
            if (matchedLangItem) {
                defaultContext.datoLocale = matchedLangItem.datoLocale;
                defaultContext.lang = matchedLangItem;
            }
        }
    }
    return defaultContext;
};
const reformatPath = (path) => `/${path
    .split('/')
    .filter(x => !!x)
    .join('/')}`;
const LOCAL_STORAGE_LANG_KEY = 'site_lang';
const LanguageProvider = ({ location, children, pageContext, globalData }) => {
    const { allSitePage, } = globalData;
    const site = {
        siteMetadata: {
            languages: {
                defaultLangKey: globalData.datoCmsSite.locales[0],
                langs: globalData.datoCmsSite.locales.map((locale) => ({
                    key: locale,
                    name: locale,
                    entryPointSlug: locale === globalData.datoCmsSite.locales[0] ? `/` : `/${locale}`,
                    hrefLangs: [locale],
                    datoLocale: locale,
                    geoProviderKeys: [locale]
                }))
            }
        }
    };
    const defaultLangKey = site?.siteMetadata?.languages?.defaultLangKey || 'au';
    const langs = React.useMemo(() => site?.siteMetadata?.languages?.langs || [], [site?.siteMetadata?.languages?.langs]);
    const sitePages = allSitePage.nodes;
    const url = location.pathname;
    const defaultLang = langs.find(x => x.key === defaultLangKey);
    const initialLanguage = (langs.find(x => x.datoLocale === pageContext.datoLocale)
        || defaultLang);
    if (!initialLanguage || !defaultLang) {
        // This will throw during a build, not on live
        throw new Error('No initial language could be found, please update site metadata');
    }
    /*
   * Save the entry point language to state, which can be updated later by the user manually.
   * This will allow us to keep the language switcher showing their preferred language, even if
   * they access non-localized pages, and they can manually switch/bounce back to their preferred
   * language when visiting other (localized) pages.
   * */
    const [language, setLanguage] = React.useState(initialLanguage);
    const [showModal, setShowModal] = React.useState(false);
    const [fetchedIp, setFetchedIp] = React.useState(false);
    const getLangPath = React.useCallback((path, lang, goToRootIfNoMatch) => {
        // Use the current Lang if no lang is provided (i.e. for static site links e.g. on Logo)
        const wantedLang = lang || language.key;
        let foundCurrentSitePage = sitePages.find(x => reformatPath(x.path) === reformatPath(path));
        const wantedLangAsObject = langs.find(x => x.key === wantedLang || x.datoLocale === wantedLang);
        // Try find on localized versions, i.e. base -> UK
        if (!foundCurrentSitePage) {
            const likelyMatch = sitePages.find(x => reformatPath(x.path).includes(reformatPath(path)));
            if (likelyMatch) {
                const foundLocaleToStrip = likelyMatch.path.split('/')[1];
                if (foundLocaleToStrip) {
                    const foundLang = langs.find(x => x?.key === foundLocaleToStrip);
                    if (foundLang && reformatPath(likelyMatch.path).replace(`/${foundLang.key}`, '') === reformatPath(path)) {
                        foundCurrentSitePage = likelyMatch;
                    }
                }
            }
        }
        if (wantedLangAsObject && foundCurrentSitePage && foundCurrentSitePage?.pageContext && 'id' in foundCurrentSitePage.pageContext) {
            const altLangVersion = sitePages.find((x) => {
                if (x.pageContext
                    && 'id' in x.pageContext && typeof x.pageContext.id === 'string'
                    && 'datoLocale' in x.pageContext && typeof x.pageContext.datoLocale === 'string') {
                    if (foundCurrentSitePage?.pageContext?.id) {
                        return (x.pageContext.id === foundCurrentSitePage.pageContext.id
                            && x.pageContext.datoLocale === wantedLangAsObject.datoLocale);
                    }
                }
                return false;
            });
            if (altLangVersion) {
                return altLangVersion.path;
            }
        }
        // Prefer root over the localized version
        if (wantedLangAsObject && goToRootIfNoMatch) {
            return wantedLangAsObject.entryPointSlug;
        }
        // Use the localized version by default
        if (foundCurrentSitePage) {
            return foundCurrentSitePage.path;
        }
        return path;
    }, [langs, language.key, sitePages]);
    const setChosenLanguage = useCallback((newLanguageKey, refresh = false, navigateToEquivalent = false) => {
        try {
            localStorage.setItem(LOCAL_STORAGE_LANG_KEY, newLanguageKey);
            // eslint-disable-next-line no-empty
        }
        catch (e) {
            // ignore non-working localstorage
        }
        const foundSelected = langs.find(x => x.key === newLanguageKey);
        if (foundSelected) {
            setLanguage(foundSelected);
            if (navigateToEquivalent) {
                const newUrl = getLangPath(location.pathname, newLanguageKey, true);
                navigate(newUrl);
            }
            // Potentially you might need to do both - navigate
            // to a new page **and** reload hubspot
            if (refresh) {
                // Bit hacky workaround for Hubspot's iframes - vomit
                if (newLanguageKey !== initialLanguage.key) {
                    setTimeout(() => {
                        window.location.reload();
                    }, 500);
                }
            }
        }
    }, [getLangPath, initialLanguage.key, langs, location.pathname]);
    const languageMenu = React.useMemo(() => langs.map(lang => ({
        langKey: lang.key,
        selected: language.key === lang.key,
        name: lang.name,
        link: getLangPath(location.pathname, lang.key, true),
    })), [getLangPath, langs, language.key, location.pathname]);
    const value = useMemo(() => ({
        languageMenu,
        language,
        languageKey: language.key,
        setChosenLanguage,
        currentHomePath: language.entryPointSlug,
        getLangPath,
    }), [language, languageMenu, getLangPath, setChosenLanguage]);
    const alternates = React.useMemo(() => {
        const foundCurrentSitePage = sitePages.find(x => reformatPath(x.path) === reformatPath(location.pathname));
        if (foundCurrentSitePage) {
            return sitePages.filter((x) => {
                if (x.pageContext
                    && 'id' in x.pageContext
                    && typeof x.pageContext.id === 'string'
                    && 'datoLocale' in x.pageContext && typeof x.pageContext.datoLocale === 'string') {
                    if (foundCurrentSitePage?.pageContext?.id) {
                        return (x.pageContext.id === foundCurrentSitePage.pageContext.id);
                    }
                }
                return false;
            }).map(x => ({
                path: x.path,
                pageContext: expandDatoLocaleKey(x.pageContext, langs, defaultLang),
            })).reduce((acc, val) => [
                ...acc,
                ...val.pageContext.lang.hrefLangs.map(hrefLang => ({
                    hrefLang,
                    path: val.path,
                })),
            ], []);
        }
        return [];
    }, [defaultLang, langs, location.pathname, sitePages]);
    useEffect(() => {
        /*
         * See if there's a local version to send the client
         * */
        let savedLangString;
        try {
            savedLangString = localStorage.getItem(LOCAL_STORAGE_LANG_KEY);
            // eslint-disable-next-line no-empty
        }
        catch (e) {
            // ignore non-working localstorage
        }
        const foundCurrentSitePage = sitePages.find(x => reformatPath(x.path) === reformatPath(location.pathname));
        if (savedLangString) {
            const foundSavedLangObject = langs.find(x => x.key === savedLangString);
            if (foundSavedLangObject
                && foundCurrentSitePage
                && foundCurrentSitePage.pageContext
                && 'lang' in foundCurrentSitePage.pageContext
                && typeof foundCurrentSitePage.pageContext.datoLocale === 'string'
                && foundSavedLangObject.datoLocale !== foundCurrentSitePage.pageContext.datoLocale) {
                setShowModal(true);
            }
        }
        else if (initialLanguage) {
            setChosenLanguage(initialLanguage.key);
        }
        else if (!fetchedIp) {
            setFetchedIp(true);
            try {
                const userLang = window.navigator.language;
                if (userLang.includes("-")) {
                    const [langCode, country] = userLang.split("-");
                    const foundLang = langs
                        .find(x => x.geoProviderKeys.includes(userLang));
                    if (foundLang) {
                        setChosenLanguage(foundLang.key);
                        return;
                    }
                    const foundCountry = langs
                        .find(x => x.geoProviderKeys.find(y => y.includes(country)));
                    if (foundCountry) {
                        setChosenLanguage(foundCountry.key);
                        return;
                    }
                    const foundLangCode = langs
                        .find(x => x.geoProviderKeys.find(y => y.includes(langCode)));
                    if (foundLangCode) {
                        setChosenLanguage(foundLangCode.key);
                        return;
                    }
                }
                else {
                    const foundLangCode = langs
                        .find(x => x.geoProviderKeys.find(y => y.includes(userLang)));
                    if (foundLangCode) {
                        setChosenLanguage(foundLangCode.key);
                        return;
                    }
                }
                setChosenLanguage(defaultLang.key);
            }
            catch (e) {
                setShowModal(true);
            }
        }
    }, [
        sitePages,
        defaultLangKey,
        langs,
        setChosenLanguage,
        initialLanguage,
        fetchedIp,
        url,
        language.key,
        location.pathname,
        defaultLang,
    ]);
    return (React.createElement(LanguageContext.Provider, { value: value },
        alternates.length > 0 && (React.createElement(HelmetDatoCms, null, alternates.map(alternate => (React.createElement("link", { key: alternate.hrefLang, rel: "alternate", hrefLang: alternate.hrefLang, href: `${process.env.GATSBY_REAL_BASE_URL}${alternate.path}` }))))),
        children));
};
export default LanguageProvider;
export const useLanguage = () => {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error('useLanguage is missing parent LanguageProvider.');
    }
    return ctx;
};
//# sourceMappingURL=language-provider.js.map